/**
 * Message operations hook
 * Provides message utilities and operations using React state
 */

import { useCallback } from 'react';
import type { ThreadMessage } from '@/types/messages';

export interface MessageOperationsProps {
  messages: ThreadMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ThreadMessage[]>>;
  currentThreadId: string | null;
}

export function useMessageOperations({ messages, setMessages, currentThreadId }: MessageOperationsProps) {

  /**
   * Get messages for a specific thread
   */
  const getMessagesByThread = useCallback((threadId: string): ThreadMessage[] => {
    if (threadId !== currentThreadId) {
      return [];
    }
    return messages
      .filter(msg => msg.threadId === threadId)
      .sort((a, b) => {
        // Sort by sequence number if available (from backend)
        const seqA = a.metadata?.sequenceNumber;
        const seqB = b.metadata?.sequenceNumber;
        
        if (seqA !== undefined && seqB !== undefined) {
          return seqA - seqB;
        }
        
        // Fallback to creation time
        return a.createdAt.getTime() - b.createdAt.getTime();
      });
  }, [messages, currentThreadId]);

  /**
   * Clear all messages for a specific thread
   */
  const clearThread = useCallback((threadId: string) => {
    if (threadId === currentThreadId) {
      setMessages([]);
    }
  }, [currentThreadId, setMessages]);

  /**
   * Add a message to the state
   */
  const addMessage = useCallback((message: ThreadMessage) => {
    // Only store messages for the current thread
    if (message.threadId !== currentThreadId) {
      return;
    }

    setMessages(prevMessages => {
      // Check if message already exists
      const existingIndex = prevMessages.findIndex(msg => msg.id === message.id);
      if (existingIndex >= 0) {
        // Update existing message
        const updatedMessages = [...prevMessages];
        updatedMessages[existingIndex] = message;
        return updatedMessages;
      } else {
        // Add new message
        return [...prevMessages, message];
      }
    });
  }, [currentThreadId, setMessages]);

  /**
   * Get a specific message by ID
   */
  const getMessage = useCallback((messageId: string): ThreadMessage | null => {
    return messages.find(msg => msg.id === messageId) || null;
  }, [messages]);

  /**
   * Update an existing message
   */
  const updateMessage = useCallback((
    messageId: string, 
    updater: (message: ThreadMessage) => ThreadMessage
  ): ThreadMessage | null => {
    const existing = messages.find(msg => msg.id === messageId);
    if (!existing || existing.threadId !== currentThreadId) {
      return null;
    }

    const updated = { ...updater(existing) };
    setMessages(prevMessages => 
      prevMessages.map(msg => msg.id === messageId ? updated : msg)
    );
    return updated;
  }, [messages, currentThreadId, setMessages]);

  /**
   * Get the latest message in a thread
   */
  const getLatestMessage = useCallback((threadId: string): ThreadMessage | null => {
    const threadMessages = getMessagesByThread(threadId);
    return threadMessages.length > 0 ? threadMessages[threadMessages.length - 1] : null;
  }, [getMessagesByThread]);

  /**
   * Check if a thread has any messages
   */
  const hasMessages = useCallback((threadId: string): boolean => {
    if (threadId !== currentThreadId) {
      return false;
    }
    return messages.some(msg => msg.threadId === threadId);
  }, [messages, currentThreadId]);

  /**
   * Get message count for a thread
   */
  const getMessageCount = useCallback((threadId: string): number => {
    if (threadId !== currentThreadId) {
      return 0;
    }
    return messages.filter(msg => msg.threadId === threadId).length;
  }, [messages, currentThreadId]);

  return {
    getMessagesByThread,
    clearThread,
    addMessage,
    getMessage,
    updateMessage,
    getLatestMessage,
    hasMessages,
    getMessageCount,
  };
}